import React from 'react';
import {Set} from 'immutable';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import {UserId} from 'records/User';
import {TeamId} from 'records/TeamId';
import {DepartmentId} from 'records/Department';
import {UserAvatar} from '@components/util/UserAvatar';
import {makeStyles} from '@material-ui/core/styles';
import {EMAIL_REGEX} from 'utils/regexValidators';
import SelectizeLookingDropdownArrow from 'components/utils/SelectizeLookingDropdownArrow';
import Chip from '@material-ui/core/Chip';
import {
  EXTERNAL_SHARE_ALLOW_ALL,
  EXTERNAL_SHARE_DOMAIN_ONLY,
  EXTERNAL_SHARE_PREVENT_ALL,
} from 'utils/const';
import AuthStore from 'stores/AuthStore';

interface Option {
  avatar: any;
  id: UserId | TeamId | DepartmentId;
  label: string;
  type: 'user' | 'team' | 'department' | 'email';
  value: UserId | TeamId | DepartmentId;
}

type SharePerm =
  | typeof EXTERNAL_SHARE_DOMAIN_ONLY
  | typeof EXTERNAL_SHARE_PREVENT_ALL
  | typeof EXTERNAL_SHARE_ALLOW_ALL;

interface ShareTargetInputProps {
  options: Option[];
  values: Option[];
  onChange: (options: Option[]) => void;
  onSearchChange: (search: string) => void;
  placeholder: string;
  search: string;
  loading: boolean;
  externalSharePermission: SharePerm;
  error: any;
}

export const ShareTargetInput: React.FC<ShareTargetInputProps> = props => {
  const valueSet = Set(props.values.map(v => v.value));

  const options = canShareToEmail(props.search, props.externalSharePermission)
    ? [
        {
          avatar: UserAvatar,
          id: props.search,
          value: props.search,
          type: 'email',
          label: `Add ${props.search}...`,
        } as Option,
        ...props.options,
      ]
    : props.options;

  const onChange = (e, values) => {
    const selectedOptions = values.filter(v => v.id || v.value);
    const stringValue: string | undefined = values.find(v => typeof v === 'string');

    if (!stringValue) return props.onChange(selectedOptions);
    if (valueSet.has(stringValue)) return;
    if (EMAIL_REGEX.test(stringValue) && options[0]) {
      return props.onChange([...selectedOptions, options[0]]);
    }
  };

  const classes = useStyles();
  return (
    <Autocomplete
      size="small"
      className={props.error && 'error-field'}
      classes={classes}
      multiple
      fullWidth
      loading={props.loading}
      renderInput={params => (
        <TextField
          placeholder={props.values.length === 0 ? props.placeholder : ''}
          {...params}
          size="small"
          variant="outlined"
          onChange={e => {
            props.onSearchChange(e.currentTarget.value);
          }}
          InputProps={{
            ...params.InputProps,
            value: props.search,
          }}
        />
      )}
      renderOption={renderOption}
      onChange={onChange}
      value={props.values}
      autoHighlight
      renderTags={renderItemValue}
      options={options}
      popupIcon={<SelectizeLookingDropdownArrow open={false} onShowDropdown={() => null} />}
      // Don't render selected values
      filterOptions={opts => opts.filter(o => !valueSet.has(o.value))}
      noOptionsText={noOptionsText(props.search, props.externalSharePermission)}
      onBlur={e => {
        // If there's an option to select, select it
        if (options[0]) {
          props.onChange([...props.values, options[0]]);
        }
        props.onSearchChange('');
      }}
    />
  );
};

function renderOption(item: Option) {
  return (
    <div key={item.id} className="sharing-dialog-option">
      {item.type === 'user' ? <UserAvatar user={item} /> : <item.avatar />}
      {item.label}
    </div>
  );
}

function renderItemValue(items, getTagProps) {
  return items.map((item, index) => {
    // Strip "Add ..." in email values
    const label = item.label?.replace(/^Add\s(.+)\.\.\.$/, '$1');

    return (
      <Chip
        size="small"
        key={item.id + item.value}
        variant="outlined"
        label={label}
        {...getTagProps({index})}
      />
    );
  });
}

function canShareToEmail(search: string, permission: SharePerm) {
  if (!EMAIL_REGEX.test(search)) return false;
  if (permission === EXTERNAL_SHARE_PREVENT_ALL) return false;
  if (permission === EXTERNAL_SHARE_DOMAIN_ONLY) return isValidDomain(search);
  return true;
}

const noOptionsText = (search: string, permission: SharePerm) => {
  if (EMAIL_REGEX.test(search)) {
    if (permission === EXTERNAL_SHARE_PREVENT_ALL) {
      return `Sharing by email address is not allowed.
            Please type the name(s) of individuals you would like to share this call with.`;
    } else if (permission === EXTERNAL_SHARE_DOMAIN_ONLY && isValidDomain(search)) {
      return `Sharing by email address is limited to addresses in your company's domain.
            Please try that or type the name(s) of individuals you would like to share this call with.`;
    } else {
      return 'Type to search';
    }
  } else {
    return 'Type to search';
  }
};

function isValidDomain(email: string) {
  const emailDomain = email.split('@')[1];
  const userDomain = AuthStore.getState().user.email.split('@')[1];
  return emailDomain === userDomain;
}

// TODO Remove me when dialog is changed to muiv4
const useStyles = makeStyles(() => ({
  popper: {
    // ev dialog z index is 1500
    zIndex: 1501,
  },
}));
